import {
    ChevronDown,
    ChevronRight,
    Download,
    File,
    Folder,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { z } from 'zod';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import { triggerBlobDownload } from '@apps/web/lib/tabular_export';
import { cn } from '@apps/web/lib/utils';
import {
    buildSandboxFileTree,
    type SandboxFileFolder,
    type SandboxRunFile,
} from '@apps/web/routes/_app/admin/agent-traces/sandbox_file_tree';
import { useAdminSandboxRunFiles } from '@apps/web/swr_data_fetching/data_fetching_hooks';

const PERSISTENCE_LABELS = {
    attached: 'Attached to chat',
    workspace: 'Workspace only',
};

const FILE_PREVIEW_SCHEMA = z.object({
    content: z.string(),
    truncated: z.boolean(),
    preview_max_bytes: z.number(),
});

// Inventory URLs are API-relative. Preserve the configured API path prefix
// and authenticate with the admin browser session.
async function fetchFile(url: string, signal?: AbortSignal) {
    const response = await fetch(`${getRuntimeConfig().apiUrl}${url}`, {
        credentials: 'include',
        signal,
    });
    if (!response.ok) {
        throw new Error('File bytes are unavailable.');
    }
    return response;
}

function FilePreview({ file }: { readonly file: SandboxRunFile }) {
    const isText =
        file.content_type.startsWith('text/') ||
        [
            'application/json',
            'application/x-ndjson',
            'application/xml',
        ].includes(file.content_type) ||
        /\.(?:jsonl|csv|json|txt|md|log|py|sh)$/iu.test(file.path);
    const isImage = [
        'image/png',
        'image/jpeg',
        'image/gif',
        'image/webp',
    ].includes(file.content_type);
    const preview = useSWR(
        isText ? file.preview_url : null,
        async (url: string) =>
            FILE_PREVIEW_SCHEMA.parse(await (await fetchFile(url)).json()),
    );
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);
    useEffect(() => {
        if (!isImage || !file.download_url) {
            return;
        }
        const controller = new AbortController();
        let url: string | null = null;
        void fetchFile(file.download_url, controller.signal)
            .then((response) => response.blob())
            .then((blob) => {
                if (controller.signal.aborted) {
                    return;
                }
                url = URL.createObjectURL(
                    new Blob([blob], { type: file.content_type }),
                );
                setImageUrl(url);
            })
            .catch(() => {
                if (!controller.signal.aborted) {
                    setError('Unable to preview this image.');
                }
            });
        return () => {
            controller.abort();
            if (url) {
                URL.revokeObjectURL(url);
            }
        };
    }, [file, isImage]);
    const download = async () => {
        if (!file.download_url) {
            return;
        }
        setDownloading(true);
        setError(null);
        try {
            const response = await fetchFile(file.download_url);
            triggerBlobDownload(
                await response.blob(),
                file.path.split('/').pop() ?? 'file',
            );
        } catch {
            setError('Unable to download this file.');
        } finally {
            setDownloading(false);
        }
    };
    return (
        <section
            aria-label="File preview"
            className="min-w-0 flex-1 space-y-4 p-4"
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 space-y-2">
                    <h3 className="font-mono text-sm break-all">{file.path}</h3>
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">
                            {PERSISTENCE_LABELS[file.persistence]}
                        </Badge>
                        <span className="text-admin-ink-muted text-xs tabular-nums">
                            {file.size_bytes === null
                                ? 'Size unknown'
                                : `${file.size_bytes.toLocaleString()} bytes`}
                        </span>
                    </div>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!file.download_url || downloading}
                    onClick={() => void download()}
                >
                    <Download className="mr-2 size-4" aria-hidden={true} />
                    {downloading ? 'Downloading…' : 'Download'}
                </Button>
            </div>
            {file.persistence === 'workspace' ? (
                <p className="text-admin-ink-muted text-sm">
                    This file can disappear when the sandbox is recycled or
                    expires. Preview and download require an attachment sent to
                    chat.
                </p>
            ) : null}
            {error || preview.error ? (
                <p role="alert" className="text-destructive text-sm">
                    {error ?? 'Unable to load this preview.'}
                </p>
            ) : null}
            {file.persistence !== 'workspace' && isText ? (
                preview.isLoading ? (
                    <Skeleton className="h-48 w-full" />
                ) : preview.data ? (
                    <div>
                        {preview.data.truncated ? (
                            <p className="text-admin-ink-muted mb-2 text-xs">
                                Preview truncated to{' '}
                                {preview.data.preview_max_bytes.toLocaleString()}{' '}
                                bytes. Download the full file.
                            </p>
                        ) : null}
                        <pre className="bg-admin-sunken max-h-[65vh] overflow-auto rounded-md p-4 text-xs leading-6 break-words whitespace-pre-wrap">
                            {preview.data.content}
                        </pre>
                    </div>
                ) : null
            ) : null}
            {isImage && file.download_url ? (
                imageUrl ? (
                    <img
                        src={imageUrl}
                        alt={file.path}
                        className="max-h-[65vh] max-w-full rounded-md object-contain"
                    />
                ) : !error ? (
                    <Skeleton className="h-48 w-full" />
                ) : null
            ) : null}
            {!isText && !isImage && file.download_url ? (
                <p className="text-admin-ink-muted text-sm">
                    {file.content_type === 'application/pdf'
                        ? 'Download this PDF to view it.'
                        : 'No inline preview for this file type. Download to inspect it.'}
                </p>
            ) : null}
        </section>
    );
}

function FolderTree({
    folder,
    selectedId,
    onSelect,
}: {
    readonly folder: SandboxFileFolder;
    readonly selectedId: string | null;
    readonly onSelect: (file: SandboxRunFile) => void;
}) {
    const [open, setOpen] = useState(true);
    const Icon = open ? ChevronDown : ChevronRight;
    return (
        <li>
            <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
                className="hover:bg-admin-surface-hover flex min-h-10 w-full items-center gap-2 rounded px-2 text-left text-sm"
            >
                <Icon className="size-3.5" aria-hidden={true} />
                <Folder
                    className="text-admin-ink-muted size-4"
                    aria-hidden={true}
                />
                {folder.name}
            </button>
            {open ? (
                <ul className="border-admin-border ml-3 border-l pl-2">
                    {[...folder.folders.values()]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((child) => (
                            <FolderTree
                                key={child.path}
                                folder={child}
                                selectedId={selectedId}
                                onSelect={onSelect}
                            />
                        ))}
                    {folder.files.map((file) => (
                        <li key={file.id}>
                            <button
                                type="button"
                                aria-pressed={selectedId === file.id}
                                onClick={() => onSelect(file)}
                                className={cn(
                                    'hover:bg-admin-surface-hover flex min-h-10 w-full flex-wrap items-center gap-2 rounded px-2 py-2 text-left text-xs',
                                    selectedId === file.id &&
                                        'bg-admin-purple-soft text-admin-purple',
                                )}
                            >
                                <File
                                    className="size-4 shrink-0"
                                    aria-hidden={true}
                                />
                                <span className="min-w-0 flex-1 break-all">
                                    {file.path.split('/').pop()}
                                </span>
                                <Badge
                                    variant="outline"
                                    className="text-[10px]"
                                >
                                    {PERSISTENCE_LABELS[file.persistence]}
                                </Badge>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </li>
    );
}

export function SandboxFileExplorer({ runId }: { readonly runId: string }) {
    const { data, error, isLoading } = useAdminSandboxRunFiles(runId);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const tree = useMemo(() => buildSandboxFileTree(data?.files ?? []), [data]);
    const selected = data?.files.find((file) => file.id === selectedId);
    if (isLoading) {
        return (
            <div aria-label="Loading files" className="space-y-2 p-4">
                <Skeleton className="h-10 w-72" />
                <Skeleton className="h-48 w-full" />
            </div>
        );
    }
    if (error) {
        return (
            <p role="alert" className="text-destructive p-4 text-sm">
                Unable to load sandbox files.
            </p>
        );
    }
    if (!data?.files.length) {
        return (
            <p className="text-admin-ink-muted p-6 text-sm">
                No workspace files were recorded for this run.
            </p>
        );
    }
    return (
        <div className="bg-admin-surface border-admin-border rounded-lg border">
            {data.truncated ? (
                <p
                    role="status"
                    className="text-admin-ink-muted border-b p-3 text-xs"
                >
                    File records were truncated. This inventory may be
                    incomplete.
                </p>
            ) : null}
            <div className="flex flex-col lg:flex-row">
                <nav
                    aria-label="Sandbox files"
                    className="border-admin-border max-h-[65vh] overflow-auto border-b p-2 lg:w-64 lg:shrink-0 lg:border-r lg:border-b-0"
                >
                    <ul>
                        <FolderTree
                            folder={tree}
                            selectedId={selectedId}
                            onSelect={(file) => setSelectedId(file.id)}
                        />
                    </ul>
                </nav>
                {selected ? (
                    <FilePreview key={selected.id} file={selected} />
                ) : (
                    <p className="text-admin-ink-muted flex-1 p-6 text-sm">
                        Select a file to preview its contents and persistence
                        status.
                    </p>
                )}
            </div>
        </div>
    );
}
